import { motion } from 'framer-motion';
import { HelpCircle, Plus, Edit2, Trash2, Search, ChevronDown, ChevronUp, RotateCw } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { supportApi } from '@/lib/api';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/Dialog';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

interface FAQ {
    id: number | string;
    category: string;
    question: string;
    answer: string;
    status: string;
    views: number;
}

export default function FAQManagement() {
    const [expandedFaq, setExpandedFaq] = useState<number | string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [faqs, setFaqs] = useState<FAQ[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedFaq, setSelectedFaq] = useState<FAQ | null>(null);
    const [formData, setFormData] = useState({
        question: '',
        answer: '',
        category: 'General',
        status: 'published' as 'published' | 'draft',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchFAQs();
    }, []);

    const fetchFAQs = async () => {
        try {
            setIsLoading(true);
            const response = await supportApi.getFAQs();
            const faqsData = response.data.data || [];
            
            const mappedFaqs: FAQ[] = faqsData.map((faq: any) => ({
                id: faq._id || faq.id || '',
                category: faq.category || 'General',
                question: faq.question || '',
                answer: faq.answer || '',
                status: faq.status || 'published',
                views: faq.views || 0,
            }));
            
            setFaqs(mappedFaqs);
        } catch (error: any) {
            console.error('Failed to fetch FAQs:', error);
            toast.error(error.response?.data?.message || 'Failed to load FAQs');
            setFaqs([]);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredFaqs = faqs.filter(
        (faq) =>
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.category.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleCreateClick = () => {
        setFormData({
            question: '',
            answer: '',
            category: 'General',
            status: 'published',
        });
        setShowCreateModal(true);
    };

    const handleEditClick = (faq: FAQ) => {
        setSelectedFaq(faq);
        setFormData({
            question: faq.question,
            answer: faq.answer,
            category: faq.category,
            status: faq.status as 'published' | 'draft',
        });
        setShowEditModal(true);
    };

    const handleDeleteClick = (faq: FAQ) => {
        setSelectedFaq(faq);
        setShowDeleteModal(true);
    };

    const handleCreateFAQ = async () => {
        if (!formData.question.trim() || !formData.answer.trim()) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setIsSubmitting(true);
            await supportApi.createFAQ(formData);
            toast.success('FAQ created successfully');
            setShowCreateModal(false);
            fetchFAQs();
        } catch (error: any) {
            console.error('Failed to create FAQ:', error);
            toast.error(error.response?.data?.message || 'Failed to create FAQ');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateFAQ = async () => {
        if (!formData.question.trim() || !formData.answer.trim()) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (!selectedFaq) return;

        try {
            setIsSubmitting(true);
            await supportApi.updateFAQ(selectedFaq.id.toString(), formData);
            toast.success('FAQ updated successfully');
            setShowEditModal(false);
            setSelectedFaq(null);
            fetchFAQs();
        } catch (error: any) {
            console.error('Failed to update FAQ:', error);
            toast.error(error.response?.data?.message || 'Failed to update FAQ');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteFAQ = async () => {
        if (!selectedFaq) return;

        try {
            setIsSubmitting(true);
            await supportApi.deleteFAQ(selectedFaq.id.toString());
            toast.success('FAQ deleted successfully');
            setShowDeleteModal(false);
            setSelectedFaq(null);
            fetchFAQs();
        } catch (error: any) {
            console.error('Failed to delete FAQ:', error);
            toast.error(error.response?.data?.message || 'Failed to delete FAQ');
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleExpand = (id: number | string) => {
        setExpandedFaq(expandedFaq === id ? null : id);
    };

    const categories = Array.from(new Set(faqs.map((f) => f.category)));

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
        >
            {/* Header */}
            <motion.div variants={itemVariants} className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-3">
                        <HelpCircle className="w-7 h-7 text-violet-400" />
                        FAQ Management
                    </h1>
                    <p className="text-slate-400 mt-1">Manage frequently asked questions</p>
                </div>
                <Button variant="default" onClick={handleCreateClick}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add New FAQ
                </Button>
            </motion.div>

            {/* Stats */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                            <HelpCircle className="w-5 h-5 text-violet-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{faqs.length}</p>
                            <p className="text-xs text-slate-400">Total FAQs</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <Badge variant="success">Published</Badge>
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{faqs.filter((f) => f.status === 'published').length}</p>
                            <p className="text-xs text-slate-400">Published</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <Badge variant="warning">Draft</Badge>
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{faqs.filter((f) => f.status === 'draft').length}</p>
                            <p className="text-xs text-slate-400">Drafts</p>
                        </div>
                    </div>
                </Card>

                <Card variant="glass" className="p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <span className="text-xs">📊</span>
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{categories.length}</p>
                            <p className="text-xs text-slate-400">Categories</p>
                        </div>
                    </div>
                </Card>
            </motion.div>

            {/* Search */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search FAQs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                        />
                    </div>
                </Card>
            </motion.div>

            {/* FAQs List */}
            <motion.div variants={itemVariants}>
                <Card variant="glass" className="p-6">
                    <h3 className="text-lg font-semibold mb-4">All FAQs</h3>
                    {isLoading ? (
                        <div className="text-center py-12">
                            <RotateCw className="w-8 h-8 text-violet-400 animate-spin mx-auto mb-4" />
                            <p className="text-slate-400">Loading FAQs...</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredFaqs.map((faq) => (
                            <div
                                key={faq.id}
                                className="border border-slate-700 rounded-lg overflow-hidden hover:border-slate-600 transition-colors"
                            >
                                <div
                                    className="p-4 cursor-pointer flex items-center justify-between"
                                    onClick={() => toggleExpand(faq.id)}
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <Badge variant="default" className="text-xs">
                                                {faq.category}
                                            </Badge>
                                            <Badge variant={faq.status === 'published' ? 'success' : 'warning'}>
                                                {faq.status}
                                            </Badge>
                                            <span className="text-xs text-slate-500">{faq.views.toLocaleString()} views</span>
                                        </div>
                                        <h4 className="font-medium">{faq.question}</h4>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditClick(faq);
                                            }}
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(faq);
                                            }}
                                        >
                                            <Trash2 className="w-4 h-4 text-red-400" />
                                        </Button>
                                        {expandedFaq === faq.id ? (
                                            <ChevronUp className="w-5 h-5 text-slate-400" />
                                        ) : (
                                            <ChevronDown className="w-5 h-5 text-slate-400" />
                                        )}
                                    </div>
                                </div>
                                {expandedFaq === faq.id && (
                                    <div className="px-4 pb-4 pt-2 border-t border-slate-700 bg-slate-800/30">
                                        <p className="text-sm text-slate-300">{faq.answer}</p>
                                    </div>
                                )}
                            </div>
                            ))}
                        </div>
                    )}
                </Card>
            </motion.div>

            {/* Create FAQ Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Create New FAQ</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Question *</label>
                            <input
                                type="text"
                                value={formData.question}
                                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                                placeholder="Enter question..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Answer *</label>
                            <textarea
                                value={formData.answer}
                                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                                rows={5}
                                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 resize-none"
                                placeholder="Enter answer..."
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Category</label>
                                <input
                                    type="text"
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                                    placeholder="e.g., Account, Payments..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Status</label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'published' | 'draft' })}
                                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                                >
                                    <option value="published">Published</option>
                                    <option value="draft">Draft</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="default" onClick={handleCreateFAQ} disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create FAQ'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit FAQ Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit FAQ</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Question *</label>
                            <input
                                type="text"
                                value={formData.question}
                                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                                placeholder="Enter question..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Answer *</label>
                            <textarea
                                value={formData.answer}
                                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                                rows={5}
                                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 resize-none"
                                placeholder="Enter answer..."
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Category</label>
                                <input
                                    type="text"
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                                    placeholder="e.g., Account, Payments..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Status</label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'published' | 'draft' })}
                                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                                >
                                    <option value="published">Published</option>
                                    <option value="draft">Draft</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditModal(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="default" onClick={handleUpdateFAQ} disabled={isSubmitting}>
                            {isSubmitting ? 'Updating...' : 'Update FAQ'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-red-400">Delete FAQ</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-slate-300">
                            Are you sure you want to delete this FAQ? This action cannot be undone.
                        </p>
                        {selectedFaq && (
                            <div className="mt-4 p-3 bg-slate-800 rounded-lg">
                                <p className="font-medium text-sm">{selectedFaq.question}</p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteFAQ} disabled={isSubmitting}>
                            {isSubmitting ? 'Deleting...' : 'Delete FAQ'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </motion.div>
    );
}
